import { apiBase } from './apiBase';
import { apiFetch, refreshOnce } from './api';
import { queryClient } from './queryClient';
import { useAuthStore } from '../store/useAuthStore';
import { readFileAsBase64, type UploadProgressHandler } from './fileUpload';

/**
 * Client for the AI Chat assistant (CLE-31).
 *
 * The backend streams the assistant response over Server-Sent Events. We use
 * `fetch` with a streaming reader (rather than the EventSource API) because we
 * need to POST the conversation body and send an Authorization header — neither
 * of which EventSource supports.
 */

/**
 * An authoritative summary of a tool call, built from the REAL backend tool
 * result (not the model's prose). Rendered as a card under the assistant
 * message so the ground truth of a write (e.g. task_create) is always visible
 * even if the model narrates something different or nothing at all (CLE-438).
 */
export interface ToolResultCard {
  /** Tool name, e.g. 'task_create' / 'task_query'. */
  tool: string;
  /** True when the backend returned ok:true (a real, committed write). */
  ok: boolean;
  /** Task identifier returned by the backend, e.g. 'TASK-007'. */
  identifier?: string;
  /** Task title returned by the backend. */
  title?: string;
  /** Number of rows a query returned. */
  count?: number;
  /** Error message the backend returned, if the tool failed. */
  error?: string;
}

export interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  /** Authoritative tool-result cards for this assistant turn (CLE-438). */
  toolResults?: ToolResultCard[];
  /**
   * In Auto mode, the model(s) that wrote this answer, in order: one entry,
   * or two when the server escalated mid-answer. Only kept for the live view.
   */
  autoModels?: string[];
}

/**
 * Distil a raw backend tool result into a `ToolResultCard`, or `null` for tools
 * we don't surface (reads other than queries, etc.). Only reflects what the
 * backend actually returned — never the model's text (CLE-438).
 */
export function toolResultToCard(tool: string, result: unknown): ToolResultCard | null {
  if (result == null || typeof result !== 'object') return null;
  const r = result as Record<string, unknown>;

  if (typeof r.error === 'string') {
    return { tool, ok: false, error: r.error };
  }

  if (tool === 'task_create' || tool === 'task_update') {
    const task = r.task as Record<string, unknown> | undefined;
    if (r.ok === true && task) {
      return {
        tool,
        ok: true,
        identifier: typeof task.identifier === 'string' ? task.identifier : undefined,
        title: typeof task.title === 'string' ? task.title : undefined,
      };
    }
  }

  if (tool === 'task_query') {
    const tasks = r.tasks;
    if (Array.isArray(tasks)) {
      return { tool, ok: true, count: tasks.length };
    }
  }

  return null;
}

/** A file (invoice PDF/image) staged in S3 and referenced in a chat message. */
export interface ChatAttachment {
  id: string;
  fileName: string;
  contentType: string;
  size?: number;
}

interface Envelope<T> {
  success: true;
  data: T;
}

export type ChatTier = 'haiku' | 'sonnet' | 'opus';

export interface ChatStatus {
  enabled: boolean;
  /** The model the chat runs on when nobody picked one (the effective default id). */
  model: string;
  /**
   * The tiers THIS user may run: the role's ceiling narrowed to the customer's
   * Firmio subscription (set in the Firmio Console). Absent on older backends.
   */
  tiers?: ChatTier[];
  /** What the chat opens on when nothing is saved: 'auto' or a model id. */
  defaultModel?: string;
}

/**
 * Selectable chat models (CLE-46). The backend validates the chosen id against
 * the same allow-list, so keep these two in sync with ALLOWED_MODELS in
 * backend/src/routes/chat.ts.
 */
export type ChatModelId = 'auto' | 'claude-haiku-5-5' | 'claude-sonnet-5-5' | 'claude-opus-5-5';

export interface ChatModelOption {
  id: ChatModelId;
  label: string;
  description: string;
}

/**
 * Current generation as of 2026-10-09 (Anthropic list prices per MTok in/out):
 * Haiku 5.5 $0.10/$0.50 (prompts over 100K tokens: $0.50/$2.50), Sonnet 5.5
 * $2/$10, Opus 5.5 $4/$20. The 5.5 ids are fixed (no date suffix).
 * The backend price table (backend/src/lib/ai/pricing.ts) must know every id
 * listed here, otherwise usage is metered at the pessimistic Opus fallback.
 */
export const CHAT_MODELS: ChatModelOption[] = [
  {
    // Not a model: the server picks Haiku/Sonnet/Opus per message
    // (backend/src/lib/ai/chatModelRouter.ts) and moves up one tier when the
    // cheaper model fails mid-answer.
    id: 'auto',
    label: 'Auto',
    description: 'Alege modelul dupa mesaj: Haiku pentru simplu, Sonnet sau Opus pentru complex',
  },
  {
    id: 'claude-haiku-5-5',
    label: 'Haiku 5.5',
    description: 'Rapid si foarte ieftin',
  },
  {
    id: 'claude-sonnet-5-5',
    label: 'Sonnet 5.5',
    description: 'Mai capabil, de ~20x mai scump ca Haiku',
  },
  {
    id: 'claude-opus-5-5',
    label: 'Opus 5.5',
    description: 'Cel mai inteligent, 2x mai scump ca Sonnet',
  },
];

/**
 * Default model — Sonnet (CLE-450). Haiku was the default for cost, but in
 * production it repeatedly answered task-creation requests WITHOUT calling any
 * tool (zero task_create invocations in the server audit log) and fabricated
 * "created and verified" confirmations with invented identifiers/UUIDs.
 * Reliable tool use is worth the per-token cost; Haiku stays selectable.
 */
export const DEFAULT_CHAT_MODEL: ChatModelId = 'claude-sonnet-5-5';

export function isChatModelId(value: unknown): value is ChatModelId {
  return CHAT_MODELS.some((m) => m.id === value);
}

/**
 * Models that used to be selectable, mapped to their successor, so a saved
 * preference for a retired option keeps its intent (cheap stays cheap) instead
 * of silently falling back to the default.
 */
const RETIRED_CHAT_MODELS: Record<string, ChatModelId> = {
  'claude-haiku-4-5-20251001': 'claude-haiku-5-5',
};

/** Short display name of a model id ("Haiku 5.5"), or the id itself. */
export function chatModelLabel(id: string): string {
  return CHAT_MODELS.find((m) => m.id === id)?.label ?? id;
}

/** Resolve a stored model preference to a selectable id, or null. */
export function resolveStoredChatModel(value: unknown): ChatModelId | null {
  if (isChatModelId(value)) return value;
  if (typeof value === 'string' && value in RETIRED_CHAT_MODELS) return RETIRED_CHAT_MODELS[value];
  return null;
}

/**
 * RBAC ceiling on the model (aiAssistant.modelAccess in Setări → Roluri și
 * permisiuni; mirrors backend/src/lib/ai/modelAccess.ts): 'haiku' = Haiku only,
 * 'sonnet' = Haiku and Sonnet, 'both' = every model, Opus included. Auto is
 * always offered — the server caps its picks to the same ceiling.
 */
export type ChatModelAccess = 'haiku' | 'sonnet' | 'both';

const MODEL_RANK: Record<ChatModelId, number> = {
  auto: -1,
  'claude-haiku-5-5': 0,
  'claude-sonnet-5-5': 1,
  'claude-opus-5-5': 2,
};

function maxRankFor(access: ChatModelAccess): number {
  return access === 'both' ? 2 : access === 'sonnet' ? 1 : 0;
}

/** May this user pick `id` by hand? (Admins pass access 'both'.) */
export function isChatModelAllowedFor(id: ChatModelId, access: ChatModelAccess): boolean {
  return MODEL_RANK[id] <= maxRankFor(access);
}

/** The picker options this user may choose from, in CHAT_MODELS order. */
export function chatModelsFor(access: ChatModelAccess): ChatModelOption[] {
  return CHAT_MODELS.filter((m) => isChatModelAllowedFor(m.id, access));
}

const TIER_MODEL_IDS: Record<ChatTier, ChatModelId> = {
  haiku: 'claude-haiku-5-5',
  sonnet: 'claude-sonnet-5-5',
  opus: 'claude-opus-5-5',
};

/**
 * The picker options for a set of tiers (what the server reports in
 * ChatStatus.tiers): Auto plus the tier ids, in CHAT_MODELS order. Combined
 * with `chatModelsFor` (the role) by intersection in the chat pages.
 */
export function chatModelsForTiers(tiers: readonly ChatTier[]): ChatModelOption[] {
  const ids = new Set<ChatModelId>(['auto', ...tiers.map((t) => TIER_MODEL_IDS[t])]);
  return CHAT_MODELS.filter((m) => ids.has(m.id));
}

/** The model the picker should start on, given what the server says and the allowed options. */
export function initialChatModel(
  status: ChatStatus | null,
  options: readonly ChatModelOption[],
): ChatModelId {
  const wanted = status?.defaultModel;
  if (wanted && options.some((o) => o.id === wanted)) return wanted as ChatModelId;
  return 'auto';
}

/** Conversation summary for the history sidebar (CLE-45). */
export interface ConversationSummary {
  id: string;
  userId: string | null;
  title: string | null;
  pinned: boolean;
  createdAt: string;
  updatedAt: string;
  messageCount: number;
  preview: string;
}

/** A full conversation (all turns) returned when loading from history. */
export interface Conversation {
  id: string;
  userId: string | null;
  title: string | null;
  pinned: boolean;
  createdAt: string;
  updatedAt: string;
  turns: ChatMessage[];
}

const ATTACHMENT_TYPES = [
  'application/pdf',
  'image/png',
  'image/jpeg',
  'image/jpg',
  'image/webp',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/msword',
  'application/vnd.ms-excel',
  'text/plain',
  'text/csv',
  'text/markdown',
];

export const MAX_ATTACHMENT_BYTES = 10 * 1024 * 1024;

// Browsers leave `file.type` empty for .md and sometimes for Office files;
// the extension settles it.
const EXT_TYPES: Record<string, string> = {
  pdf: 'application/pdf',
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  webp: 'image/webp',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  doc: 'application/msword',
  xls: 'application/vnd.ms-excel',
  txt: 'text/plain',
  csv: 'text/csv',
  md: 'text/markdown',
};

export function attachmentContentType(file: File): string {
  const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
  return file.type.toLowerCase() || EXT_TYPES[ext] || '';
}

export function isSupportedAttachment(file: File): boolean {
  return ATTACHMENT_TYPES.includes(attachmentContentType(file));
}

/** Upload an invoice file to S3 staging and return its attachment reference. */
export async function uploadChatAttachment(
  file: File,
  onProgress?: UploadProgressHandler,
): Promise<ChatAttachment> {
  const dataBase64 = await readFileAsBase64(file, onProgress);
  const res = await apiFetch<Envelope<ChatAttachment>>('/api/chat/attachments', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: JSON.stringify({
      fileName: file.name,
      contentType: attachmentContentType(file),
      dataBase64,
    }),
  });
  return res.data;
}

function authHeaders(): Record<string, string> {
  const token = useAuthStore.getState().accessToken;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

/** Whether the assistant is configured (ANTHROPIC_API_KEY set on the backend). */
export async function getChatStatus(): Promise<ChatStatus> {
  const res = await apiFetch<Envelope<ChatStatus>>('/api/chat/status', {
    headers: authHeaders(),
  });
  return res.data;
}

// --- Conversation history (CLE-45) -----------------------------------------

/** List past conversations, optionally filtered by a free-text query. */
export async function listConversations(query?: string): Promise<ConversationSummary[]> {
  const qs = query && query.trim() ? `?q=${encodeURIComponent(query.trim())}` : '';
  const res = await apiFetch<Envelope<{ conversations: ConversationSummary[] }>>(
    `/api/chat/conversations${qs}`,
    { headers: authHeaders() },
  );
  return res.data.conversations;
}

/** Load a full conversation (all turns) by id. */
export async function getConversation(id: string): Promise<Conversation> {
  const res = await apiFetch<Envelope<{ conversation: Conversation }>>(
    `/api/chat/conversations/${encodeURIComponent(id)}`,
    { headers: authHeaders() },
  );
  return res.data.conversation;
}

/** Rename and/or pin a conversation. */
export async function updateConversation(
  id: string,
  changes: { title?: string; pinned?: boolean },
): Promise<Conversation> {
  const res = await apiFetch<Envelope<{ conversation: Conversation }>>(
    `/api/chat/conversations/${encodeURIComponent(id)}`,
    {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify(changes),
    },
  );
  return res.data.conversation;
}

/** Delete a conversation from history. */
export async function deleteConversation(id: string): Promise<void> {
  await apiFetch<Envelope<{ deleted: true }>>(`/api/chat/conversations/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    headers: authHeaders(),
  });
}

/** Callbacks invoked as the assistant streams its response. */
export interface StreamHandlers {
  onDelta: (text: string) => void;
  /** The server rewrote the streamed answer (e.g. removed an invented card): show `text` instead. */
  onReplace?: (text: string) => void;
  onToolUse?: (name: string, input: Record<string, unknown>) => void;
  onToolResult?: (name: string, result: unknown) => void;
  /** `code` is set for machine-detectable failures (e.g. AI_BUDGET_EXCEEDED). */
  onError?: (message: string, code?: string) => void;
  /** stopReason plus the server-assigned conversationId for this thread. */
  onDone?: (stopReason: string | null, conversationId?: string) => void;
  /**
   * The model answering this turn: sent once at the start, and again if Auto
   * escalated mid-answer (`escalated`). `reason` is set in Auto mode.
   */
  onModel?: (info: { model: string; auto: boolean; reason?: string; escalated?: boolean }) => void;
}

interface SsePacket {
  event: string;
  data: unknown;
}

/**
 * Send the conversation and stream the assistant reply. Returns a function that
 * aborts the in-flight request (used to cancel a running response).
 */
export interface StreamChatOptions {
  /**
   * The project whose „Context” tab this conversation runs in: the backend
   * injects that project's context document into the system prompt and has
   * the assistant save everything the user tells it about the project.
   */
  projectContextId?: string;
}

export function streamChat(
  messages: ChatMessage[],
  handlers: StreamHandlers,
  attachments?: ChatAttachment[],
  conversationId?: string,
  model?: ChatModelId,
  options: StreamChatOptions = {},
): () => void {
  const controller = new AbortController();
  const apiUrl = apiBase();

  const body = JSON.stringify({
    messages,
    ...(conversationId ? { conversationId } : {}),
    ...(model ? { model } : {}),
    ...(options.projectContextId ? { projectContextId: options.projectContextId } : {}),
    ...(attachments && attachments.length
      ? {
          attachments: attachments.map((a) => ({
            id: a.id,
            fileName: a.fileName,
            contentType: a.contentType,
          })),
        }
      : {}),
  });

  // The chat stream uses raw `fetch` (not apiFetch) to read the SSE body, so it
  // needs its own expired-session recovery (CLE-439): on a 401 we refresh the
  // token once and replay the request, instead of showing a hard error that
  // forced the user to refresh the whole page.
  const send = (): Promise<Response> =>
    fetch(`${apiUrl}/api/chat`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body,
      signal: controller.signal,
    });

  void (async () => {
    try {
      let res = await send();

      if (res.status === 401) {
        const token = await refreshOnce();
        if (token) res = await send();
      }

      if (!res.ok || !res.body) {
        // 403 = RBAC (aiAssistant.canAccess or someone else's thread). The
        // backend's message is user-facing; also refetch the permission
        // matrix so the sidebar/composer catch up with the role change.
        if (res.status === 403) {
          const body = await res.json().catch(() => null);
          const err = body?.error as { code?: string; message?: string } | undefined;
          void queryClient.invalidateQueries({ queryKey: ['permissions', 'me'] });
          handlers.onError?.(
            err?.message ||
              'Nu ai acces la Firmio AI. Un administrator ți-l poate acorda din Setări → Roluri și permisiuni.',
            err?.code ?? 'PERMISSION_DENIED',
          );
          handlers.onDone?.('error');
          return;
        }
        handlers.onError?.(`Cererea a esuat: ${res.status}`);
        handlers.onDone?.('error');
        return;
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let sawDone = false;

      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        let sepIndex: number;
        while ((sepIndex = buffer.indexOf('\n\n')) !== -1) {
          const rawEvent = buffer.slice(0, sepIndex);
          buffer = buffer.slice(sepIndex + 2);
          const packet = parseSsePacket(rawEvent);
          if (packet) {
            if (packet.event === 'done') sawDone = true;
            dispatch(packet, handlers);
          }
        }
      }

      // The server closed the stream without a `done` event (e.g. backend
      // restarted mid-response). Without this, the UI would stay in
      // "streaming" forever and keep an empty assistant turn in the history.
      if (!sawDone) {
        handlers.onError?.('Conexiunea cu asistentul s-a intrerupt. Trimite mesajul din nou.');
        handlers.onDone?.('disconnected');
      }
    } catch (err) {
      if (controller.signal.aborted) return; // user-initiated cancel
      handlers.onError?.(err instanceof Error ? err.message : 'Eroare de retea');
      handlers.onDone?.('error');
    }
  })();

  return () => controller.abort();
}

function parseSsePacket(raw: string): SsePacket | null {
  let event = 'message';
  const dataLines: string[] = [];
  for (const line of raw.split('\n')) {
    if (line.startsWith('event:')) event = line.slice('event:'.length).trim();
    else if (line.startsWith('data:')) dataLines.push(line.slice('data:'.length).trim());
  }
  if (!dataLines.length) return null;
  try {
    return { event, data: JSON.parse(dataLines.join('')) };
  } catch {
    return null;
  }
}

function dispatch(packet: SsePacket, handlers: StreamHandlers): void {
  const data = packet.data as Record<string, unknown>;
  switch (packet.event) {
    case 'delta':
      handlers.onDelta(String(data.text ?? ''));
      break;
    case 'replace':
      handlers.onReplace?.(String(data.text ?? ''));
      break;
    case 'tool_use':
      handlers.onToolUse?.(String(data.name ?? ''), (data.input as Record<string, unknown>) ?? {});
      break;
    case 'tool_result':
      handlers.onToolResult?.(String(data.name ?? ''), data.result);
      break;
    case 'error':
      handlers.onError?.(
        String(data.message ?? 'Eroare'),
        typeof data.code === 'string' ? data.code : undefined,
      );
      break;
    case 'model':
      handlers.onModel?.({
        model: String(data.model ?? ''),
        auto: data.auto === true,
        ...(typeof data.reason === 'string' ? { reason: data.reason } : {}),
        ...(data.escalated === true ? { escalated: true } : {}),
      });
      break;
    case 'done':
      handlers.onDone?.(
        (data.stopReason as string | null) ?? null,
        typeof data.conversationId === 'string' ? data.conversationId : undefined,
      );
      break;
    default:
      break;
  }
}
